import { createError, dateToMonthYear, isMonthYearAfterOrEqual } from '@common/neo.tax';
import { Stack } from '@ui-components/neo.tax';
import React, { useMemo } from 'react';
import { Outlet } from 'react-router-dom';

import { RdContextProvider } from '@neo.tax/rd/context';
import {
  getFiscalYearSubsetPeriod,
  useFiscalYearOrFail,
  PeriodName,
  useSelectedProductOptional,
  useSelectedFiscalYearIdOptional,
  useSelectedPeriodNameOptional,
  ProductType,
} from '@neo.tax/shared';

import { RdSideNav } from '../rd-side-nav';
import { Container } from './rd-home-layout.styled';

export const RdHomeLayout = (): React.ReactElement | null => {
  const product = useSelectedProductOptional();
  const periodName = useSelectedPeriodNameOptional();
  const fiscalYearId = useSelectedFiscalYearIdOptional();

  if (product !== ProductType.UsRd || !periodName || !fiscalYearId) {
    throw createError('RdHomeLayoutError', 'Product is not RD or period name or fiscal year id not found');
  }

  if (periodName !== PeriodName.A) {
    throw createError('RdHomeLayoutError', 'Period is not annual for R&D');
  }

  const { fiscalYear } = useFiscalYearOrFail({ fiscalYearId });

  // confirm that the fiscal year product type is RD
  if (fiscalYear.productType !== ProductType.UsRd || !fiscalYear.enabledDatetime) {
    throw createError('RdHomeLayoutError', 'Fiscal year product type is not RD or is not enabled');
  }

  const { isNowPastPeriodEndDate, period } = useMemo(() => {
    const period = getFiscalYearSubsetPeriod(fiscalYear.taxYearStartDate, fiscalYear.taxYearEndDate, periodName);
    const today = new Date();
    const isNowPastPeriodEndDate = isMonthYearAfterOrEqual(dateToMonthYear(today), period.endMonthYear);
    return { period, isNowPastPeriodEndDate };
  }, [fiscalYear.taxYearStartDate, fiscalYear.taxYearEndDate, periodName]);

  return (
    <RdContextProvider
      fiscalYearId={fiscalYearId}
      fiscalYear={fiscalYear}
      periodName={periodName}
      period={period}
      product={product}
      isNowPastPeriodEndDate={isNowPastPeriodEndDate}
    >
      <Container>
        <RdSideNav />
        <Stack fullHeight fullWidth overflow="y-auto">
          <Outlet />
        </Stack>
      </Container>
    </RdContextProvider>
  );
};
